import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { debounce } from 'lodash';
import BigNumber from 'bignumber.js';
import { MIN_VALUES } from '../../../constants/minValues';
import RustCalculatorApi from '../../../api/rustApi';
import UserSettingsApi from '../../../api/userSettingsApi';
import type { CalculatorRequest, RiskUnit } from '../../../types/calculator';
import { computeStockFees, formatStockFeeBreakdown, type StockFeeParams } from '../utils/stockFeeCalculator.ts';
import { computeMultiExits, defaultTpExits } from '../../../utils/multiExits';

const CALCULATION_DELAY = 100;
const LOCALSTORAGE_DEBOUNCE = 500;
const API_SYNC_DEBOUNCE = 2000;
const LOCALSTORAGE_KEY = 'stocksCalculatorSettings';

BigNumber.config({
    EXPONENTIAL_AT: 1e+9,
    DECIMAL_PLACES: 8,
    ROUNDING_MODE: BigNumber.ROUND_DOWN,
    FORMAT: {
        decimalSeparator: '.',
        groupSeparator: '',
        groupSize: 0,
        secondaryGroupSize: 0,
        fractionGroupSeparator: '',
        fractionGroupSize: 0
    }
});

const defaultValues = {
    risk: new BigNumber(10),
    riskUnit: 'fixed',
    accountBalance: new BigNumber(25000),
    nominalRR: new BigNumber(2),
    maxLeverage: new BigNumber(1),
    safetyBufferR: new BigNumber(1),
    entryPrice: new BigNumber(150.00),
    stopLoss: new BigNumber(148.00),
    brokerMode: 'perShare',
    brokerRate: new BigNumber(0.005),
    brokerMin: new BigNumber(1.00),
    brokerMaxPct: new BigNumber(1.0),
    exchangeRate: new BigNumber(0),
    clearingRate: new BigNumber(0.0002),
    clearingMaxPct: new BigNumber(0.5),
    secRate: new BigNumber(20.60),          // $ per $1M sold (regulators publish as $/million)
    finraRate: new BigNumber(0.195),        // $ per 1,000 shares sold
    finraMax: new BigNumber(8.30),
    finraCatRate: new BigNumber(0.033),     // $ per 1,000 shares sold
    // Multiple take-profits (partial exits). Disabled → single TP as before.
    multiTpEnabled: false,
    tpCount: 3,
    tpMode: 'percent',
    tpExits: defaultTpExits(),
};

const defaultMetrics = {
    positionSize: '--',
    quantity: '--',
    leverage: '--',
    margin: '--',
    effectiveTake: '--',
    nominalTake: '--',
    effectiveStop: '--',
    nominalStop: '--',
    effectiveRR: '--',
    friction: '--',
    nominalRR: '--',
    optimizedRR: '--',
    priceLevels: {
        liquidationPrice: '--',
        breakEven: '--',
        takeProfit: '--',
        optimizedTakeProfit: '--',
        stopLoss: '--'
    },
    tradingFees: {
        entryFee: '--',
        tpFee: '--',
        slFee: '--'
    },
    totalFees: {
        takeProfitFees: '--',
        stopLossFees: '--'
    },
    feeBreakdown: {
        broker: '--',
        exchange: '--',
        clearing: '--',
        sec: '--',
        finra: '--',
        finraCat: '--',
        total: '--'
    },
    multiExit: null,
};

const serializeValues = (values: typeof defaultValues) => {
    const serialized: Record<string, any> = {};
    Object.entries(values).forEach(([key, value]) => {
        if (key === 'tpExits' && Array.isArray(value)) {
            serialized[key] = value.map((e: any) => ({
                alloc: e.alloc instanceof BigNumber ? e.alloc.toString() : e.alloc,
                rMultiple: e.rMultiple instanceof BigNumber ? e.rMultiple.toString() : e.rMultiple,
            }));
        } else if (value instanceof BigNumber) {
            serialized[key] = value.toString();
        } else {
            serialized[key] = value;
        }
    });
    return JSON.stringify(serialized);
};

const deserializeValues = (serializedData: string) => {
    try {
        const parsed = JSON.parse(serializedData);
        const deserialized: any = { ...defaultValues };
        Object.entries(parsed).forEach(([key, value]) => {
            if (key === 'updatedAt' || !(key in defaultValues)) return;
            if (key === 'riskUnit' || key === 'brokerMode' || key === 'tpMode') {
                deserialized[key] = value as string;
            } else if (key === 'tpExits') {
                deserialized[key] = Array.isArray(value)
                    ? value.map((e: any) => ({
                        alloc: new BigNumber(e.alloc),
                        rMultiple: new BigNumber(e.rMultiple),
                    }))
                    : defaultTpExits();
            } else if (typeof value === 'string' && key !== 'riskUnit' && key !== 'brokerMode' && key !== 'tpMode') {
                try {
                    deserialized[key] = new BigNumber(value);
                } catch (e) {
                    console.warn(`[Stocks] Failed to parse BigNumber for ${key}:`, e);
                }
            } else {
                deserialized[key] = value;
            }
        });
        // Migrate old raw rates to human-readable format
        if (deserialized.secRate.lt(1)) {
            // Old format: 0.0000278 (raw multiplier) → new: 27.80 ($ per $1M)
            deserialized.secRate = deserialized.secRate.times(1_000_000);
        }
        if (deserialized.finraRate.lt(0.01)) {
            // Old format: 0.000166 (raw per-share) → new: 0.166 ($ per 1K shares)
            deserialized.finraRate = deserialized.finraRate.times(1_000);
        }
        // finraCatRate: new field, handled by defaultValues spread (missing key gets default)

        return deserialized;
    } catch (e) {
        console.warn('[Stocks] Failed to deserialize values:', e);
        return defaultValues;
    }
};

const loadFromLocalStorage = (): typeof defaultValues => {
    try {
        const stored = localStorage.getItem(LOCALSTORAGE_KEY);
        if (stored) {
            return deserializeValues(stored);
        }
    } catch (e) {
        console.warn('[Stocks] Failed to load from localStorage:', e);
    }
    return defaultValues;
};

const saveToLocalStorage = (values: typeof defaultValues) => {
    try {
        const serialized = serializeValues(values);
        const settingsWithTimestamp = {
            ...JSON.parse(serialized),
            updatedAt: new Date().toISOString()
        };
        localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(settingsWithTimestamp));
    } catch (e) {
        console.warn('[Stocks] Failed to save to localStorage:', e);
    }
};

export const useStocksCalculator = () => {
    const [values, setValues] = useState(() => loadFromLocalStorage());
    const [metrics, setMetrics] = useState(defaultMetrics);
    const [isCalculating, setIsCalculating] = useState(false);
    const [extremePriceField, setExtremePriceField] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});
    const [apiSyncStatus, setApiSyncStatus] = useState('idle');
    const lastRequestId = useRef(0);
    const settingsLoadedFromApi = useRef(false);
    const initialLoadComplete = useRef(false);

    const debouncedSaveToLocalStorage = useMemo(
        () => debounce((valuesToSave: typeof defaultValues) => {
            saveToLocalStorage(valuesToSave);
        }, LOCALSTORAGE_DEBOUNCE),
        []
    );

    const debouncedSyncToApi = useMemo(
        () => debounce(async (valuesToSync: typeof defaultValues) => {
            const canSync = await UserSettingsApi.canSyncSettings();
            if (!canSync) return;
            try {
                setApiSyncStatus('syncing');
                const serializedSettings = serializeValues(valuesToSync);
                const success = await UserSettingsApi.updateUserSettings(
                    JSON.parse(serializedSettings)
                );
                setApiSyncStatus(success ? 'idle' : 'error');
                if (!success) setTimeout(() => setApiSyncStatus('idle'), 5000);
            } catch (error) {
                console.error('[Stocks] Error syncing to API:', error);
                setApiSyncStatus('error');
                setTimeout(() => setApiSyncStatus('idle'), 5000);
            }
        }, API_SYNC_DEBOUNCE),
        []
    );

    const loadSettingsFromApi = useCallback(async () => {
        if (settingsLoadedFromApi.current) return;
        try {
            setApiSyncStatus('loading');
            const apiSettings = await UserSettingsApi.getUserSettings();
            if (apiSettings && apiSettings.stocks_calculator_settings) {
                const currentLocalStorage = localStorage.getItem(LOCALSTORAGE_KEY);
                let shouldUpdate = true;
                if (currentLocalStorage) {
                    try {
                        const currentParsed = JSON.parse(currentLocalStorage);
                        const apiTimestamp = apiSettings.updatedAt ? new Date(apiSettings.updatedAt).getTime() : 0;
                        const localTimestamp = currentParsed.updatedAt ? new Date(currentParsed.updatedAt).getTime() : 0;
                        shouldUpdate = !localTimestamp || apiTimestamp > localTimestamp;
                    } catch (e) {
                        shouldUpdate = true;
                    }
                }
                if (shouldUpdate) {
                    const deserializedSettings = deserializeValues(JSON.stringify(apiSettings.stocks_calculator_settings));
                    setValues(deserializedSettings);
                    const settingsWithTimestamp = {
                        ...apiSettings.stocks_calculator_settings,
                        updatedAt: apiSettings.updatedAt || new Date().toISOString()
                    };
                    localStorage.setItem(LOCALSTORAGE_KEY, JSON.stringify(settingsWithTimestamp));
                }
            }
            setApiSyncStatus('idle');
        } catch (error) {
            console.error('[Stocks] Error loading from API:', error);
            setApiSyncStatus('error');
            setTimeout(() => setApiSyncStatus('idle'), 5000);
        } finally {
            settingsLoadedFromApi.current = true;
            initialLoadComplete.current = true;
        }
    }, []);

    useEffect(() => {
        const timer = setTimeout(() => { loadSettingsFromApi(); }, 100);
        return () => clearTimeout(timer);
    }, [loadSettingsFromApi]);

    useEffect(() => {
        debouncedSaveToLocalStorage(values);
        if (initialLoadComplete.current) {
            debouncedSyncToApi(values);
        }
        return () => {
            debouncedSaveToLocalStorage.cancel();
            debouncedSyncToApi.cancel();
        };
    }, [values, debouncedSaveToLocalStorage, debouncedSyncToApi]);

    const checkMinValueErrors = useCallback((currentValues) => {
        const errors = {};
        if (currentValues.risk && currentValues.risk.isLessThan(MIN_VALUES.risk)) errors.risk = true;
        if (currentValues.nominalRR && currentValues.nominalRR.isLessThan(MIN_VALUES.nominalRR)) errors.nominalRR = true;
        if (currentValues.accountBalance && currentValues.accountBalance.isLessThan(MIN_VALUES.accountBalance)) errors.accountBalance = true;
        if (currentValues.maxLeverage && currentValues.maxLeverage.isLessThan(MIN_VALUES.maxLeverage)) errors.maxLeverage = true;
        if (currentValues.safetyBufferR && currentValues.safetyBufferR.isLessThan(MIN_VALUES.safetyBufferR)) errors.safetyBufferR = true;
        return errors;
    }, []);

    useEffect(() => {
        const newErrors = checkMinValueErrors(values);
        setValidationErrors(newErrors);
    }, [values, checkMinValueErrors]);

    const validateField = useCallback((field, value) => {
        if (!value || value.isLessThanOrEqualTo(0)) return true;
        return false;
    }, []);

    const isValidPriceRange = (entryPrice, stopLoss, sourceField = null) => {
        if (!entryPrice || !stopLoss || entryPrice.isZero() || stopLoss.isZero()) return false;

        const bigger = BigNumber.max(entryPrice, stopLoss);
        const smaller = BigNumber.min(entryPrice, stopLoss);
        const priceRatio = bigger.dividedBy(smaller);

        if (priceRatio.gt(20)) {
            if (sourceField) {
                setExtremePriceField(sourceField === 'entryPrice' ? 'stopLoss' : 'entryPrice');
            } else {
                setExtremePriceField(entryPrice.gt(stopLoss) ? 'entryPrice' : 'stopLoss');
            }
            return false;
        }

        const priceDiff = entryPrice.minus(stopLoss).abs();
        const relativeDiff = priceDiff.dividedBy(BigNumber.max(entryPrice, stopLoss));
        if (relativeDiff.lt(0.0001)) {
            if (sourceField) {
                setExtremePriceField(sourceField === 'entryPrice' ? 'stopLoss' : 'entryPrice');
            } else {
                setExtremePriceField(entryPrice.gt(stopLoss) ? 'entryPrice' : 'stopLoss');
            }
            return false;
        }

        setExtremePriceField(null);
        return true;
    };

    const updateValues = useCallback((newValues) => {
        if (typeof newValues === 'function') {
            setValues(prevValues => {
                const actualNewValues = newValues(prevValues);
                const sanitizedValues = { ...actualNewValues };
                let hasChanges = false;
                if (sanitizedValues.nominalRR && sanitizedValues.nominalRR.isLessThan?.(MIN_VALUES.nominalRR)) {
                    sanitizedValues.nominalRR = MIN_VALUES.nominalRR; hasChanges = true;
                }
                if (sanitizedValues.risk && sanitizedValues.risk.isLessThan?.(MIN_VALUES.risk)) {
                    sanitizedValues.risk = MIN_VALUES.risk; hasChanges = true;
                }
                if (sanitizedValues.accountBalance && sanitizedValues.accountBalance.isLessThan?.(MIN_VALUES.accountBalance)) {
                    sanitizedValues.accountBalance = MIN_VALUES.accountBalance; hasChanges = true;
                }
                if (sanitizedValues.maxLeverage && sanitizedValues.maxLeverage.isLessThan?.(MIN_VALUES.maxLeverage)) {
                    sanitizedValues.maxLeverage = MIN_VALUES.maxLeverage; hasChanges = true;
                }
                if (sanitizedValues.safetyBufferR && sanitizedValues.safetyBufferR.isLessThan?.(MIN_VALUES.safetyBufferR)) {
                    sanitizedValues.safetyBufferR = MIN_VALUES.safetyBufferR; hasChanges = true;
                }
                return hasChanges ? sanitizedValues : actualNewValues;
            });
            return true;
        }

        if (!newValues) return false;

        const errors = {};
        Object.entries(newValues).forEach(([field, value]) => {
            if (value instanceof BigNumber && validateField(field, value)) {
                errors[field] = true;
            }
        });

        const changedField = Object.keys(newValues).find(key => {
            const isOldBigNumber = values[key]?.constructor?.name === 'BigNumber';
            const isNewBigNumber = newValues[key]?.constructor?.name === 'BigNumber';
            if (isOldBigNumber && isNewBigNumber) return !values[key].isEqualTo(newValues[key]);
            return values[key] !== newValues[key];
        });

        if (newValues.entryPrice && newValues.stopLoss) {
            isValidPriceRange(newValues.entryPrice, newValues.stopLoss, changedField);
        }

        setValues(newValues);
        return Object.keys(errors).length === 0;
    }, [validateField, values]);

    const calculateMetrics = useCallback(async () => {
        const currentRequestId = lastRequestId.current + 1;
        lastRequestId.current = currentRequestId;

        if (!isValidPriceRange(values.entryPrice, values.stopLoss)) {
            setMetrics(defaultMetrics);
            return;
        }

        try {
            setIsCalculating(true);

            // --- Frontend iterative position sizing ---
            const riskAmount = values.riskUnit === 'percent'
                ? values.accountBalance.times(values.risk).dividedBy(100)
                : values.risk;

            const stopDistance = values.entryPrice.minus(values.stopLoss).abs();
            const isLong = values.entryPrice.gt(values.stopLoss);

            const feeParams: StockFeeParams = {
                brokerMode: values.brokerMode,
                brokerRate: values.brokerRate,
                brokerMin: values.brokerMin,
                brokerMaxPct: values.brokerMaxPct,
                exchangeRate: values.exchangeRate,
                clearingRate: values.clearingRate,
                clearingMaxPct: values.clearingMaxPct,
                secRate: values.secRate.dividedBy(1_000_000),
                finraRate: values.finraRate.dividedBy(1_000),
                finraMax: values.finraMax,
                finraCatRate: values.finraCatRate.dividedBy(1_000),
            };

            // Iterative sizing (3 iterations for convergence)
            let flooredShares = riskAmount.dividedBy(stopDistance).integerValue(BigNumber.ROUND_DOWN);
            for (let i = 0; i < 3; i++) {
                if (flooredShares.lte(0)) break;
                const feeResult = computeStockFees(flooredShares, values.entryPrice, values.stopLoss, feeParams, isLong);
                flooredShares = riskAmount.minus(feeResult.total).dividedBy(stopDistance).integerValue(BigNumber.ROUND_DOWN);
            }

            // Edge case: risk too small for even 1 share
            if (flooredShares.lte(0)) {
                setMetrics(defaultMetrics);
                return;
            }

            // Compute final fee result for converged shares (at stop loss for sizing)
            const finalFeeResult = computeStockFees(flooredShares, values.entryPrice, values.stopLoss, feeParams, isLong);

            // Derive equivalent fee percentages from actual dollar fees
            const posValue = flooredShares.times(values.entryPrice);
            const exitValue = flooredShares.times(values.stopLoss);
            const entryPct = posValue.gt(0)
                ? finalFeeResult.entrySideTotal.dividedBy(posValue).times(100)
                : new BigNumber(0);
            const exitPct = exitValue.gt(0)
                ? finalFeeResult.exitSideTotal.dividedBy(exitValue).times(100)
                : new BigNumber(0);

            const requestBody: CalculatorRequest = {
                entryPrice: values.entryPrice.toString(),
                stopLoss: values.stopLoss.toString(),
                risk: values.risk.toString(),
                accountBalance: values.accountBalance.toString(),
                riskUnit: values.riskUnit as RiskUnit,
                nominalRR: values.nominalRR.toString(),
                fees: {
                    entry: entryPct.toString(),
                    exit: exitPct.toString(),
                },
                margin: {
                    safety: values.safetyBufferR.toString(),
                    maxLeverage: values.maxLeverage ? values.maxLeverage.toString() : "1"
                },
            };

            let normalizedRiskUnit = values.riskUnit;
            if (values.riskUnit === '%' || values.riskUnit === 'percentage') normalizedRiskUnit = 'percent';
            else if (values.riskUnit === '$' || values.riskUnit === 'dollar') normalizedRiskUnit = 'fixed';
            else if (!['percent', 'fixed'].includes(values.riskUnit)) normalizedRiskUnit = 'fixed';
            requestBody.riskUnit = normalizedRiskUnit;

            const data = await RustCalculatorApi.calculate(requestBody);

            if (currentRequestId !== lastRequestId.current) return;

            if (!data.priceLevels || !data.tradingFees || !data.totalFees) {
                throw new Error('Invalid response structure from server');
            }

            // Override quantity with our integer shares
            data.quantity = flooredShares.toString();
            data.positionSize = flooredShares.times(values.entryPrice).toString();

            // Compute fee breakdown for display using optimized TP from backend
            const optimizedTP = new BigNumber(data.priceLevels.optimizedTakeProfit);
            const feeResultTP = computeStockFees(flooredShares, values.entryPrice, optimizedTP, feeParams, isLong);
            const feeBreakdown = formatStockFeeBreakdown(feeResultTP);

            const formattedData = {
                ...data,
                positionSize: new BigNumber(data.positionSize),
                quantity: new BigNumber(data.quantity),
                leverage: new BigNumber(data.leverage),
                margin: new BigNumber(data.margin),
                effectiveTake: new BigNumber(data.effectiveTake),
                nominalTake: new BigNumber(data.nominalTake),
                effectiveStop: new BigNumber(data.effectiveStop),
                nominalStop: new BigNumber(data.nominalStop),
                effectiveRR: new BigNumber(data.effectiveRR),
                friction: new BigNumber(data.friction),
                nominalRR: new BigNumber(data.nominalRR),
                optimizedRR: new BigNumber(data.optimizedRR),
                priceLevels: {
                    liquidationPrice: data.priceLevels.liquidationPrice,
                    breakEven: data.priceLevels.breakEven,
                    takeProfit: data.priceLevels.takeProfit,
                    optimizedTakeProfit: data.priceLevels.optimizedTakeProfit,
                    stopLoss: data.priceLevels.stopLoss
                },
                tradingFees: {
                    entryFee: data.tradingFees.entryFee === '--' ? new BigNumber(0) : new BigNumber(data.tradingFees.entryFee),
                    tpFee: data.tradingFees.tpFee === '--' ? new BigNumber(0) : new BigNumber(data.tradingFees.tpFee),
                    slFee: data.tradingFees.slFee === '--' ? new BigNumber(0) : new BigNumber(data.tradingFees.slFee),
                },
                totalFees: {
                    takeProfitFees: data.totalFees.takeProfitFees === '--' ? new BigNumber(0) : new BigNumber(data.totalFees.takeProfitFees),
                    stopLossFees: data.totalFees.stopLossFees === '--' ? new BigNumber(0) : new BigNumber(data.totalFees.stopLossFees),
                },
                feeBreakdown,
            };

            // priceDecimals intentionally omitted — computeMultiExits' own default
            // (8dp) preserves full precision. Deriving it from breakEven's incidental
            // decimal count used to silently truncate target prices below what's
            // needed to reproduce the stated R-multiple.
            const multiExit = values.multiTpEnabled
                ? computeMultiExits({
                    entryPrice: values.entryPrice,
                    stopDistance: values.entryPrice.minus(values.stopLoss).abs(),
                    tpPathFee: formattedData.totalFees.takeProfitFees,
                    exits: values.tpExits,
                    mode: values.tpMode,
                    quantity: formattedData.quantity,
                    riskAmount: formattedData.effectiveStop,
                    breakEven: formattedData.priceLevels.breakEven,
                    optimizedTakeProfit: formattedData.priceLevels.optimizedTakeProfit,
                    nominalRR: formattedData.nominalRR,
                    side: values.entryPrice.gt(values.stopLoss) ? 'long' : 'short',
                })
                : null;

            setMetrics({ ...formattedData, multiExit });
        } catch (err) {
            console.error('[Stocks] Calculation error:', err);
            setMetrics(defaultMetrics);
        } finally {
            if (currentRequestId === lastRequestId.current) {
                setIsCalculating(false);
            }
        }
    }, [values]);

    const debouncedCalculateMetrics = useMemo(
        () => debounce(calculateMetrics, CALCULATION_DELAY),
        [calculateMetrics]
    );

    useEffect(() => {
        debouncedCalculateMetrics();
        return () => debouncedCalculateMetrics.cancel();
    }, [debouncedCalculateMetrics]);

    return {
        values,
        setValues: updateValues,
        metrics,
        calculateMetrics: debouncedCalculateMetrics,
        isCalculating,
        validationErrors,
        extremePriceField,
        apiSyncStatus,
        loadSettingsFromApi,
    };
};
